/**
 * Configuration Mixin
 * Provides easy access to application configuration in Vue components
 */

import appConfig from '../config/appConfig';

export default {
  computed: {
    /**
     * Get application configuration
     * @returns {Object} Application configuration instance
     */
    $config() {
      return appConfig;
    },

    /**
     * Get currency configuration
     * @returns {Object} Currency configuration
     */
    $currency() {
      const config = this.$store?.getters?.['billingApp/billingAppConfig'];
      if (!config) {
        return appConfig.getCurrency();
      }

      return {
        ...appConfig.getCurrency(),
        code: config.currencyCode,
        symbol: config.currencySymbol,
        decimalPlaces: config.currencyDecimalPlaces,
      };
    },

    /**
     * Get currency code
     * @returns {string} Currency code
     */
    $currencyCode() {
      return this.$currency.code;
    },

    /**
     * Get currency symbol
     * @returns {string} Currency symbol
     */
    $currencySymbol() {
      return this.$currency.symbol;
    },

    /**
     * Get locale configuration
     * @returns {Object} Locale configuration
     */
    $locale() {
      return appConfig.getLocale();
    },

    /**
     * Get country name
     * @returns {string} Country name
     */
    $countryName() {
      return appConfig.getCountryName();
    },

    /**
     * Get application name
     * @returns {string} Application name
     */
    $appName() {
      return appConfig.getAppName();
    },

    /**
     * Get company name
     * @returns {string} Company name
     */
    $companyName() {
      return appConfig.getCompanyName();
    },

    /**
     * Get billing mode from runtime/app config
     * @returns {string} Billing mode
     */
    $billingMode() {
      return appConfig.getBillingMode();
    },
  },

  methods: {
    /**
     * Format currency amount
     * @param {number} amount - Amount to format
     * @param {Object} options - Formatting options
     * @returns {string} Formatted currency string
     */
    $formatCurrency(amount, options = {}) {
      const { decimalPlaces } = this.$currency;
      return appConfig.formatCurrency(amount, { decimalPlaces, ...options });
    },

    /**
     * Format currency with short format for large numbers
     * @param {number} amount - Amount to format
     * @returns {string} Formatted currency string with short format
     */
    $formatLargeCurrency(amount) {
      return appConfig.formatCurrency(amount, { useShortFormat: true });
    },

    /**
     * Format number according to locale
     * @param {number} value - Number to format
     * @param {number} decimals - Number of decimal places
     * @returns {string} Formatted number string
     */
    $formatNumber(value, decimals = 0) {
      return appConfig.formatNumber(value, decimals);
    },

    /**
     * Format date according to locale
     * @param {Date|string} date - Date to format
     * @param {Object} options - Formatting options
     * @returns {string} Formatted date string
     */
    $formatDate(date, options = {}) {
      return appConfig.formatDate(date, options);
    },

    /**
     * Format date and time according to locale
     * @param {Date|string} date - Date to format
     * @param {Object} options - Formatting options
     * @returns {string} Formatted date and time string
     */
    $formatDateTime(date, options = {}) {
      return appConfig.formatDateTime(date, options);
    },

    /**
     * Check if a feature is enabled
     * @param {string} featureName - Name of the feature
     * @returns {boolean} True if feature is enabled
     */
    $isFeatureEnabled(featureName) {
      return appConfig.isFeatureEnabled(featureName);
    },
  },
};
